import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {FileIdBadge} from '@client/pages/payroll/components/FileIdBadge';
import {FileIdValidation} from '@client/pages/payroll/types';

export function FileIdValidationAlert({
  fileIdValidation,
  onClose,
  onSwapFileIds,
  onConfirm,
}: {
  fileIdValidation: FileIdValidation;
  onClose: () => void;
  onSwapFileIds: () => void;
  onConfirm?: () => void;
}) {
  return (
    <AlertDialog
      open={fileIdValidation.show}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {fileIdValidation.mode === 'swapped'
              ? 'Files appear to be assigned to the wrong IDs'
              : 'File ID mismatch found'}
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogDescription>
          {fileIdValidation.mode === 'swapped' ? (
            <div className='space-y-3'>
              <div>
                We detected that your uploaded files likely have their file IDs swapped compared to
                your saved configuration. The columns referenced for each expected file ID appear
                under a different uploaded file:
              </div>
              <div className='mt-2 space-y-2'>
                {(fileIdValidation.suspectedSwappedPairs || []).map(({expectedId, actualId}) => (
                  <div
                    key={expectedId}
                    className='flex items-center gap-2 p-2 bg-gray-50 border border-gray-200 rounded-sm'
                  >
                    <div className='text-sm text-gray-700'>
                      Expected <FileIdBadge fileId={expectedId} /> appears in uploaded file{' '}
                      <FileIdBadge fileId={actualId} />
                    </div>
                  </div>
                ))}
              </div>
              <div className='mt-2 text-sm text-gray-700'>
                You can apply a quick fix to swap the uploaded file IDs to match your configuration.
              </div>
            </div>
          ) : (
            <div>
              The following file IDs were expected based on your existing configuration but were not
              found in the newly uploaded files:
              <div className={'mt-4 space-y-2'}>
                {fileIdValidation.missingFileIds.map((fileId) => (
                  <div
                    key={fileId}
                    className='flex items-center gap-2 p-2 bg-gray-50 text-internal-brand-navy font-medium border border-gray-200 rounded-sm'
                  >
                    {fileId}
                  </div>
                ))}
              </div>
              <div className='mt-4 text-sm text-gray-700'>
                Your current uploaded files have the following file IDs:
                <div className='mt-2 space-y-2'>
                  {fileIdValidation.currentFileIds.map((fileId) => (
                    <div
                      key={fileId}
                      className='flex items-center gap-2 p-2 bg-gray-50 text-internal-brand-navy font-medium border border-gray-200 rounded-sm'
                    >
                      {fileId}
                    </div>
                  ))}
                </div>
              </div>
              <div className='mt-4 text-sm text-gray-700'>
                To resolve this, you can either:
                <ul className='list-disc list-inside mt-2 space-y-1'>
                  <li>Re-upload the files with the expected file IDs.</li>
                  <li>
                    Remove mappings that reference these missing file IDs from your configuration.
                  </li>
                </ul>
              </div>
            </div>
          )}
        </AlertDialogDescription>
        <AlertDialogFooter>
          {fileIdValidation.mode === 'swapped' ? (
            <>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onSwapFileIds}
                className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'
              >
                Swap IDs to match configuration
              </AlertDialogAction>
            </>
          ) : (
            <AlertDialogAction
              onClick={onConfirm}
              className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'
            >
              OK
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
